import { useEffect, useState } from 'react';
import useApiRequest from '../../../../lib/hooks/useRequest';
import Preloader from '../../../../ui/components/ui/preloader';
import { useArchiveList } from '../../hooks/use-archive-list';
import { CreateProductRequestDto, UpdateArchiveRequestDto } from '../../types/request';
import {
  BillingTermMetaResponseDto,
  ProductDetailsResponseDto,
  ProductListItemResponseDto,
  ProductMetaResponseDto,
  ProductResponseDto
} from '../../types/response';
import ArchiveConfirmDialog from '../archive-confirm-dialog/archive-confirm-dialog';
import ListToolbar from '../list-toolbar/list-toolbar';
import ProductFormDialog from '../product-form-dialog/product-form-dialog';
import ProductList from './product-list';

/** Data owner of the products tab. */
const ProductListWrapper = () => {
  const { apiRequest } = useApiRequest();
  const { items, pageCount, isLoading, reload, filter, handleFilterChange, pagination, handlePaginationChange } =
    useArchiveList<ProductListItemResponseDto>('products/admin');

  // The product form renders one price row per billing term, so the tab waits for them.
  const [billingTerms, setBillingTerms] = useState<BillingTermMetaResponseDto[] | null>(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductDetailsResponseDto | null>(null);
  const [archiveCandidate, setArchiveCandidate] = useState<ProductListItemResponseDto | null>(null);

  useEffect(() => {
    const fetchMeta = async () => {
      const res = await apiRequest<ProductMetaResponseDto>('products/admin/meta', 'GET');
      if (res.data) setBillingTerms(res.data.billingTerms);
    };
    fetchMeta();
  }, [apiRequest]);

  const handleCreate = () => {
    setEditingProduct(null);
    setIsFormOpen(true);
  };

  const handleEdit = async (item: ProductListItemResponseDto) => {
    const res = await apiRequest<ProductDetailsResponseDto>(`products/admin/${item._id}`, 'GET');
    if (!res.data) return;
    setEditingProduct(res.data);
    setIsFormOpen(true);
  };

  const handleSubmit = async (payload: CreateProductRequestDto): Promise<boolean> => {
    const res = editingProduct
      ? await apiRequest<ProductResponseDto>(`products/admin/${editingProduct._id}`, 'PATCH', {
          body: payload,
          toast: { toastText: 'Produkt wurde aktualisiert.' }
        })
      : await apiRequest<ProductResponseDto>('products/admin', 'POST', {
          body: payload,
          toast: { toastText: 'Produkt wurde erstellt.' }
        });
    if (res.error) return false;
    await reload();
    return true;
  };

  const handleArchiveConfirm = async (): Promise<boolean> => {
    if (!archiveCandidate) return false;
    const body: UpdateArchiveRequestDto = { isArchived: !archiveCandidate.isArchived };
    const res = await apiRequest<ProductResponseDto>(`products/admin/${archiveCandidate._id}/archive`, 'PATCH', {
      body,
      toast: { toastText: body.isArchived ? 'Produkt wurde archiviert.' : 'Produkt wurde wiederhergestellt.' }
    });
    if (res.error) return false;
    await reload();
    return true;
  };

  if (!billingTerms) return <Preloader />;

  return (
    <div className="flex flex-col gap-5">
      <ListToolbar filter={filter} onFilterChange={handleFilterChange} createLabel="Neues Produkt" onCreate={handleCreate} />

      <ProductList
        products={items}
        filter={filter}
        pagination={pagination}
        pageCount={pageCount}
        onPaginationChange={handlePaginationChange}
        isLoading={isLoading}
        onEdit={handleEdit}
        onArchiveToggle={setArchiveCandidate}
      />

      <ProductFormDialog
        isOpen={isFormOpen}
        onOpenChange={setIsFormOpen}
        billingTerms={billingTerms}
        product={editingProduct}
        onSubmit={handleSubmit}
      />

      <ArchiveConfirmDialog
        candidate={archiveCandidate}
        entityLabel="Produkt"
        onClose={() => setArchiveCandidate(null)}
        onConfirm={handleArchiveConfirm}
      />
    </div>
  );
};

export default ProductListWrapper;
